import React from "react";
import { useNavigate } from "react-router-dom";
import Button from "@mui/material/Button";
import ButtonGroup from "@mui/material/ButtonGroup";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import ClickAwayListener from "@mui/material/ClickAwayListener";
import Grow from "@mui/material/Grow";
import Paper from "@mui/material/Paper";
import Popper from "@mui/material/Popper";
import MenuItem from "@mui/material/MenuItem";
import MenuList from "@mui/material/MenuList";
import { usePatrolsStore } from "./patrolsStore/patrolsStore";
import { downloadSiteReport } from "./patrolsUtils/downloadSiteReport";

const OPTIONS = ["Apply Filters", "Download Report"];

export const ApplyFiltersButtonPatrols = () => {
  const { filtersPatrols, getPatrolsHistory, patrolsHistoryLoading, setPage } =
    usePatrolsStore();
  const navigate = useNavigate();

  const [open, setOpen] = React.useState(false);
  const anchorRef = React.useRef<HTMLDivElement>(null);
  const [option, setOption] = React.useState(OPTIONS[0]);
  const [downloadingReport, setDownloadingReport] = React.useState(false);

  const siteSelected =
    filtersPatrols.site && filtersPatrols.site.length > 0;
  const isApplyDisabled =
    patrolsHistoryLoading ||
    (!filtersPatrols.parent_group &&
      (!filtersPatrols.site || filtersPatrols.site.length === 0));

  const handleApplyFilters = () => {
    setPage(0);
    getPatrolsHistory(navigate);
  };

  const handleDownloadReport = async () => {
    if (!siteSelected || !filtersPatrols.site[0]) return;
    setDownloadingReport(true);
    try {
      await downloadSiteReport(
        filtersPatrols.site[0],
        filtersPatrols.start_date,
        filtersPatrols.end_date
      );
    } finally {
      setDownloadingReport(false);
    }
  };

  const handlePrimaryClick = () => {
    if (option === "Apply Filters") {
      handleApplyFilters();
    } else {
      handleDownloadReport();
    }
  };

  const handleMenuItemClick = (
    event: React.MouseEvent<HTMLLIElement, MouseEvent>,
    index: number
  ) => {
    setOption(OPTIONS[index]);
    setOpen(false);
  };

  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen);
  };

  const handleClose = (event: Event) => {
    if (
      anchorRef.current &&
      anchorRef.current.contains(event.target as HTMLElement)
    ) {
      return;
    }
    setOpen(false);
  };

  // No site selected: single Apply Filters button only
  if (!siteSelected) {
    return (
      <div className="pt-[7px] z-10">
        <button
          className={`shadow-button border-2 rounded font-mulish text-white text-base p-1.5 w-[120px] ${
            isApplyDisabled
              ? "bg-gray-400 border-gray-400 cursor-not-allowed"
              : "bg-actuate-orange border-actuate-orange hover:bg-[#E67300]"
          }`}
          onClick={handleApplyFilters}
          disabled={isApplyDisabled}
        >
          {patrolsHistoryLoading ? "Loading..." : "Apply Filters"}
        </button>
      </div>
    );
  }

  // Site selected: split button with Apply Filters + Download Report
  return (
    <div className="pt-[7px] z-10">
      <ButtonGroup
        variant="contained"
        ref={anchorRef}
        aria-label="Patrol actions"
        color="warning"
        sx={{ fontFamily: "Mulish" }}
      >
        {option === "Apply Filters" ? (
          <Button
            onClick={handlePrimaryClick}
            disabled={isApplyDisabled}
            sx={{
              backgroundColor: "#FF8400",
              "&:hover": { backgroundColor: "#E67300" },
              width: "140px",
              fontFamily: "Mulish",
            }}
          >
            {patrolsHistoryLoading ? "Loading..." : "Apply Filters"}
          </Button>
        ) : (
          <Button
            onClick={handlePrimaryClick}
            disabled={downloadingReport}
            sx={{
              color: "white",
              backgroundColor: "#FF8400",
              "&:hover": {
                backgroundColor: "#E67300",
                color: "white",
              },
              width: "200px",
              fontFamily: "Mulish",
            }}
          >
            {downloadingReport ? "Downloading..." : "Download Report"}
          </Button>
        )}
        <Button
          size="small"
          aria-controls={open ? "split-button-menu" : undefined}
          aria-expanded={open ? "true" : undefined}
          aria-label="select action"
          aria-haspopup="menu"
          sx={{
            backgroundColor: "#FF8400",
            "&:hover": { backgroundColor: "#E67300" },
            fontFamily: "Mulish",
          }}
          onClick={handleToggle}
        >
          <ArrowDropDownIcon />
        </Button>
      </ButtonGroup>
      <Popper
        sx={{ zIndex: 1 }}
        open={open}
        anchorEl={anchorRef.current}
        role={undefined}
        transition
        disablePortal
      >
        {({ TransitionProps, placement }) => (
          <Grow
            {...TransitionProps}
            style={{
              transformOrigin:
                placement === "bottom" ? "center top" : "center bottom",
            }}
          >
            <Paper>
              <ClickAwayListener onClickAway={handleClose}>
                <MenuList
                  className="absolute"
                  id="split-button-menu"
                  autoFocusItem
                  sx={{ fontFamily: "Mulish" }}
                >
                  {OPTIONS.map((opt, index) => (
                    <MenuItem
                      key={opt}
                      selected={option === opt}
                      onClick={(event) => handleMenuItemClick(event, index)}
                      sx={{ fontFamily: "Mulish" }}
                    >
                      {opt}
                    </MenuItem>
                  ))}
                </MenuList>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
    </div>
  );
};
